/**
 * SFrame202 — Plan Payment Order (dt-2): hub with five child checklist routes.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { useSettlement } from '../../contexts/SettlementContext';
import {
  countPlanPaymentSectionsDone,
  derivePlanPaymentSectionsFromChildren,
  reconcilePlanPaymentHubFromChildren,
  type PlanPaymentHubSectionKey,
} from './debts/planPaymentHub';

type SectionCard = {
  hubKey: PlanPaymentHubSectionKey;
  title: string;
  pathForEstate: (estateId: string) => string;
};

const SECTION_CARDS: SectionCard[] = [
  {
    hubKey: 's1',
    title: 'Understand Legal Priority',
    pathForEstate: (e) => `/sframe-202-s1/${e}`,
  },
  {
    hubKey: 's2',
    title: 'Categorize Debt Priority',
    pathForEstate: (e) => `/sframe-202-s2/${e}`,
  },
  {
    hubKey: 's3',
    title: 'Assess Estate Liquidity',
    pathForEstate: (e) => `/sframe-202-s3/${e}`,
  },
  {
    hubKey: 's4',
    title: 'Draft Payment Schedule',
    pathForEstate: (e) => `/sframe-202-s4/${e}`,
  },
  {
    hubKey: 's5',
    title: 'Finalize Payment Plan',
    pathForEstate: (e) => `/sframe-202-s5/${e}`,
  },
];

export default function SFrame202() {
  const { estateId } = useParams<{ estateId: string }>();
  const navigate = useNavigate();
  const { isArchived } = useSettlement();
  const { loadFrameData, saveFrameData, updateStepProgress, error, isLoading } = useSettlementSession(
    estateId ?? null,
    { readOnly: isArchived }
  );

  const [hubSections, setHubSections] = useState({
    s1: false,
    s2: false,
    s3: false,
    s4: false,
    s5: false,
  });

  const reloadHub = useCallback(async () => {
    if (!estateId) return;
    if (isArchived) {
      const derived = await derivePlanPaymentSectionsFromChildren(loadFrameData);
      setHubSections(derived);
      return;
    }
    const merged = await reconcilePlanPaymentHubFromChildren(
      loadFrameData,
      saveFrameData,
      updateStepProgress
    );
    setHubSections(merged);
  }, [estateId, isArchived, loadFrameData, saveFrameData, updateStepProgress]);

  useEffect(() => {
    void reloadHub();
  }, [reloadHub]);

  const completedCount = countPlanPaymentSectionsDone(hubSections);
  const pct = Math.round((completedCount / 5) * 100);

  return (
    <div className="max-w-2xl mx-auto py-8 px-4">
      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <h1 className="text-2xl font-semibold text-stone-800 mb-2">Plan Payment Order</h1>

      <div className="mb-6">
        <div className="flex justify-between text-sm text-stone-500 mb-1">
          <span>{completedCount} of 5 tasks completed</span>
          <span>{pct}%</span>
        </div>
        <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
          <div
            className="h-2 bg-cyan-600 rounded-full transition-all"
            style={{ width: `${(completedCount / 5) * 100}%` }}
          />
        </div>
      </div>

      <div className="space-y-3">
        {SECTION_CARDS.map((section) => {
          const complete = !!hubSections[section.hubKey];
          const dest = estateId ? section.pathForEstate(estateId) : undefined;
          return (
            <button
              key={section.hubKey}
              type="button"
              disabled={!dest || isLoading}
              onClick={() => dest && navigate(dest)}
              className="w-full bg-white rounded-xl border border-stone-200 hover:border-cyan-500 hover:shadow-sm p-5 flex items-center gap-4 text-left transition-all disabled:opacity-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={complete}
                readOnly
                aria-hidden
                tabIndex={-1}
                className="h-5 w-5 rounded border-stone-300 text-cyan-600 pointer-events-none shrink-0"
              />
              <span className="flex-1 font-medium text-stone-800">{section.title}</span>
              <ChevronRight className="w-5 h-5 text-stone-400 shrink-0" aria-hidden />
            </button>
          );
        })}
      </div>
    </div>
  );
}
